import React, { useCallback, useEffect, useMemo, useState } from "react";

// 안전 준수 리포트 — GET /api/safety-report 를 호출해 요약·추이·항목/현장/시간대·목록을 그린다.
// 차트는 외부 라이브러리 없이 Tailwind 막대로 표현. 색은 data-viz 팔레트(검증 통과):
//   항목별(정체성)=카테고리색(안전모 blue / 조끼 aqua / 하네스 yellow), 표 뱃지와 통일.
//   추이·시간대·현장별(크기)=시퀀셜 블루 단일. 라이트 대비 경고는 막대 끝 숫자 라벨로 relief.

function pad2(n) {
  return String(n).padStart(2, "0");
}
function toStr(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}
function todayStr() {
  return toStr(new Date());
}
function monthStartStr() {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-01`;
}
function daysAgoStr(n) {
  const d = new Date();
  d.setDate(d.getDate() - (n - 1));
  return toStr(d);
}

const STATUS_LABEL = { open: "미조치", done: "조치완료" };
const DETAIL_PAGE_SIZE = 5;

// 항목 정체성 → 색 (막대 fill · 표 뱃지 동일 계열)
function itemKind(item, label) {
  const s = `${item || ""} ${label || ""}`.toUpperCase();
  if (s.includes("HELMET") || s.includes("안전모") || s.includes("헬멧")) return "helmet";
  if (s.includes("VEST") || s.includes("조끼")) return "vest";
  if (s.includes("HARNESS") || s.includes("하네스") || s.includes("안전대")) return "harness";
  return "other";
}
const ITEM_BAR = {
  helmet: "bg-[#2a78d6] dark:bg-[#3987e5]",
  vest: "bg-[#1baf7a] dark:bg-[#199e70]",
  harness: "bg-[#eda100] dark:bg-[#c98500]",
  other: "bg-slate-400 dark:bg-slate-500",
};
const ITEM_DOT = ITEM_BAR;
const ITEM_BADGE = {
  helmet: "bg-blue-50 text-blue-700 ring-blue-200 dark:bg-blue-500/15 dark:text-blue-300 dark:ring-blue-500/30",
  vest: "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-300 dark:ring-emerald-500/30",
  harness: "bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-500/15 dark:text-amber-300 dark:ring-amber-500/30",
  other: "bg-slate-100 text-slate-600 ring-slate-200 dark:bg-slate-500/15 dark:text-slate-300 dark:ring-slate-500/30",
};

const SEQ_BAR = "bg-[#2a78d6] dark:bg-[#3987e5]"; // 시퀀셜 블루(크기)

function Card({ title, right, children, className = "" }) {
  return (
    <div
      className={`rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 ${className}`}
    >
      {(title || right) && (
        <div className="mb-3 flex items-center justify-between gap-2">
          <div className="text-sm font-semibold text-gray-700 dark:text-gray-200">{title}</div>
          {right}
        </div>
      )}
      {children}
    </div>
  );
}

// 차트 카드 — 높이를 채우는 flex(제목 shrink-0 + 본문 flex-1). 스크롤 없는 대시보드용.
function ChartCard({ title, right, children, className = "" }) {
  return (
    <div className={`flex min-h-0 flex-col rounded-2xl border border-gray-200 bg-white p-3 shadow-sm dark:border-gray-700 dark:bg-gray-800 ${className}`}>
      <div className="mb-2 flex shrink-0 items-center justify-between gap-2">
        <div className="text-sm font-semibold text-gray-700 dark:text-gray-200">{title}</div>
        {right}
      </div>
      <div className="min-h-0 flex-1">{children}</div>
    </div>
  );
}

// KPI 타일. highlight=헤드라인 강조(테두리), valueSize=값 글자크기(텍스트 값은 작게).
function StatTile({ label, value, unit, accent = "text-gray-900 dark:text-white", valueSize = "text-[23px]", highlight = false, foot }) {
  return (
    <div
      className={`rounded-2xl border bg-white px-4 py-3 shadow-sm dark:bg-gray-800 ${
        highlight
          ? "border-emerald-300 ring-1 ring-emerald-200 dark:border-emerald-500/40 dark:ring-emerald-500/20"
          : "border-gray-200 dark:border-gray-700"
      }`}
    >
      <div className="text-xs font-medium text-gray-500 dark:text-gray-400">{label}</div>
      <div className="mt-1 flex items-baseline gap-1">
        <span className={`truncate font-bold leading-none tabular-nums ${valueSize} ${accent}`}>{value}</span>
        {unit && <span className="shrink-0 text-sm font-semibold text-gray-400">{unit}</span>}
      </div>
      <div className="mt-1 min-h-[14px] text-[11px] text-gray-400">{foot}</div>
    </div>
  );
}

// 가로 막대(항목별=색, 현장별=블루) — 막대 끝 숫자 라벨(대비 relief)
function HBars({ items, colorOf, emptyText }) {
  const max = Math.max(1, ...items.map((i) => i.count));
  if (!items.length) {
    return (
      <div className="flex h-full min-h-0 items-center justify-center text-sm text-gray-400">
        {emptyText || "데이터 없음"}
      </div>
    );
  }
  return (
    <div className="flex h-full min-h-0 flex-col justify-center gap-2.5 overflow-y-auto">
      {items.map((it) => {
        const pct = (it.count / max) * 100;
        return (
          <div key={it.key} className="flex items-center gap-3" title={`${it.label}: ${it.count}건`}>
            <div
              className="w-24 shrink-0 truncate text-xs text-gray-600 dark:text-gray-300"
              title={it.label}
            >
              {it.label}
            </div>
            <div className="relative h-6 flex-1 overflow-hidden rounded-lg bg-gray-100 dark:bg-gray-700/60">
              <div
                className={`absolute inset-y-0 left-0 rounded-lg ${colorOf ? colorOf(it) : SEQ_BAR}`}
                style={{ width: `${it.count ? Math.max(pct, 2) : 0}%` }}
              />
              <div className="absolute inset-y-0 right-2 flex items-center text-[11px] font-bold tabular-nums text-gray-600 dark:text-gray-300">
                {it.count}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// 세로 막대(날짜별/시간대별) — 시퀀셜 블루
function VBars({ items, emptyText, markPeak = false }) {
  const max = Math.max(1, ...items.map((i) => i.count));
  const has = items.some((i) => i.count > 0);
  // 막대가 많으면(날짜 31개 등) x축 라벨이 겹치므로 띄엄띄엄만 표시.
  const labelStep = items.length > 16 ? Math.ceil(items.length / 12) : 1;
  // 피크(최다) 막대 강조용 인덱스.
  const peakIdx = markPeak && has
    ? items.reduce((bi, it, i, a) => (it.count > a[bi].count ? i : bi), 0)
    : -1;
  if (!has) {
    return (
      <div className="flex h-full min-h-0 items-center justify-center text-sm text-gray-400">
        {emptyText || "데이터 없음"}
      </div>
    );
  }
  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* 막대: flex-1 로 카드 높이를 채우고, 컬럼 h-full 로 height% 계산되게 */}
      <div className="min-h-0 flex-1 overflow-x-auto">
        <div className="flex h-full items-end gap-[3px]">
        {items.map((it, i) => (
          <div
            key={it.key}
            className="group relative flex h-full min-w-[14px] flex-1 items-end"
            title={`${it.label}: ${it.count}건`}
          >
            {/* hover 시 값 표시(레이아웃 영향 없게 absolute) */}
            <div className="pointer-events-none absolute -top-4 left-1/2 -translate-x-1/2 text-[10px] font-bold tabular-nums text-gray-500 opacity-0 transition group-hover:opacity-100 dark:text-gray-300">
              {it.count || ""}
            </div>
            <div
              className={`w-full rounded-t-[5px] transition group-hover:brightness-110 ${
                i === peakIdx
                  ? "bg-gradient-to-t from-[#e0574f] to-[#f6a445]"
                  : "bg-gradient-to-t from-[#2a6fd0] to-[#6aa8ef] dark:from-[#2f6fc4] dark:to-[#5a9ee8]"
              }`}
              style={{ height: `${it.count ? Math.max((it.count / max) * 100, 2) : 0}%` }}
            />
          </div>
        ))}
        </div>
      </div>
      {/* x축 라벨: 막대와 같은 컬럼 폭으로 정렬 */}
      <div className="mt-1 flex shrink-0 gap-[3px]">
        {items.map((it, idx) => (
          <div
            key={it.key}
            className="min-w-[14px] flex-1 truncate text-center text-[10px] leading-tight text-gray-400"
          >
            {idx % labelStep === 0 ? it.short : ""}
          </div>
        ))}
      </div>
    </div>
  );
}

// 영역(area) 추이 차트 — 박스를 꽉 채우되(그래프 stretch), 글자/오늘값/끝점은 HTML로 분리해 왜곡 방지.
function AreaChart({ items }) {
  const has = items && items.length >= 2 && items.some((i) => i.count > 0);
  if (!has) {
    return <div className="flex h-full items-center justify-center text-sm text-gray-400">데이터 없음</div>;
  }
  const W = 620, H = 200, pad = 4, padB = 6, padT = 14;
  const n = items.length;
  const max = Math.max(...items.map((i) => i.count)) * 1.18 || 1;
  const X = (i) => pad + (i * (W - 2 * pad)) / (n - 1);
  const Y = (v) => H - padB - (v / max) * (H - padB - padT);
  let d = `M ${X(0)} ${Y(items[0].count)}`;
  for (let i = 0; i < n - 1; i++) {
    const x0 = X(i), y0 = Y(items[i].count), x1 = X(i + 1), y1 = Y(items[i + 1].count), cx = (x0 + x1) / 2;
    d += ` C ${cx} ${y0} ${cx} ${y1} ${x1} ${y1}`;
  }
  const area = `${d} L ${X(n - 1)} ${H - padB} L ${X(0)} ${H - padB} Z`;
  const step = n > 16 ? Math.ceil(n / 12) : 1;
  const lastVal = items[n - 1].count;
  const lastLeft = (X(n - 1) / W) * 100;
  const lastTop = (Y(lastVal) / H) * 100;
  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="relative min-h-0 flex-1">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="absolute inset-0 h-full w-full text-blue-600 dark:text-blue-400"
          preserveAspectRatio="none"
        >
          <defs>
            <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="currentColor" stopOpacity="0.28" />
              <stop offset="1" stopColor="currentColor" stopOpacity="0.02" />
            </linearGradient>
          </defs>
          {[0, 1, 2, 3].map((g) => {
            const gy = padT + g * ((H - padB - padT) / 3);
            return <line key={g} x1={0} y1={gy} x2={W} y2={gy} className="stroke-gray-100 dark:stroke-white/10" strokeWidth="1" vectorEffect="non-scaling-stroke" />;
          })}
          <path d={area} fill="url(#areaGrad)" />
          <path d={d} fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
        </svg>
        {/* 끝점(오늘) — HTML 이라 안 찌그러짐 */}
        <div
          className="pointer-events-none absolute h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-blue-600 ring-2 ring-white dark:bg-blue-400 dark:ring-[#0f172a]"
          style={{ left: `${lastLeft}%`, top: `${lastTop}%` }}
        />
        <div className="pointer-events-none absolute right-2 top-1 rounded-md bg-blue-50 px-2 py-0.5 text-[11px] font-bold text-blue-600 dark:bg-blue-500/15 dark:text-blue-300">
          오늘 {lastVal}건
        </div>
      </div>
      <div className="mt-1 flex shrink-0">
        {items.map((it, i) => (
          <div key={i} className="min-w-0 flex-1 truncate text-center text-[10px] text-gray-400">
            {i % step === 0 ? it.short : ""}
          </div>
        ))}
      </div>
    </div>
  );
}

// 도넛 차트 — 비율(항목별). 세그먼트 arc + 가운데 총합 + 우측 범례.
const DONUT_COLOR = { helmet: "#2563eb", vest: "#06b6d4", harness: "#f59e0b", other: "#94a3b8" };
function DonutChart({ segments }) {
  const total = segments.reduce((s, x) => s + x.value, 0);
  if (!total) {
    return <div className="flex h-full items-center justify-center text-sm text-gray-400">데이터 없음</div>;
  }
  const cx = 70, cy = 70, r = 52, thick = 16;
  let a0 = -Math.PI / 2;
  const arcs = segments.map((s, idx) => {
    const frac = s.value / total;
    const a1 = a0 + frac * 2 * Math.PI;
    const large = frac > 0.5 ? 1 : 0;
    const x0 = cx + r * Math.cos(a0), y0 = cy + r * Math.sin(a0);
    const x1 = cx + r * Math.cos(a1), y1 = cy + r * Math.sin(a1);
    const path = frac >= 0.9999
      ? `M ${cx} ${cy - r} A ${r} ${r} 0 1 1 ${cx - 0.01} ${cy - r} Z`
      : `M ${x0} ${y0} A ${r} ${r} 0 ${large} 1 ${x1} ${y1}`;
    a0 = a1;
    return <path key={idx} d={path} fill="none" stroke={s.color} strokeWidth={thick} strokeLinecap="butt" />;
  });
  return (
    <div className="flex h-full items-center justify-center gap-5">
      <svg viewBox="0 0 140 140" width="132" height="132" className="shrink-0">
        {arcs}
        <text x={cx} y={cy - 2} fontSize="22" fontWeight="800" textAnchor="middle" className="fill-gray-900 dark:fill-white">{total}</text>
        <text x={cx} y={cy + 15} fontSize="10" textAnchor="middle" className="fill-gray-400">미준수 건</text>
      </svg>
      <div className="flex flex-col gap-2 text-sm">
        {segments.map((s) => {
          const p = Math.round((100 * s.value) / total);
          return (
            <div key={s.label} className="flex items-center gap-2">
              <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: s.color }} />
              <span className="text-gray-700 dark:text-gray-200">{s.label}</span>
              <span className="ml-auto font-bold tabular-nums text-gray-900 dark:text-white">{s.value}</span>
              <span className="w-9 text-right text-xs tabular-nums text-gray-400">{p}%</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const PRESETS = [
  { key: "today", label: "오늘", range: () => [todayStr(), todayStr()] },
  { key: "d7", label: "최근 7일", range: () => [daysAgoStr(7), todayStr()] },
  { key: "d30", label: "최근 30일", range: () => [daysAgoStr(30), todayStr()] },
  { key: "month", label: "이번 달", range: () => [monthStartStr(), todayStr()] },
];

export default function SafetyReportPage() {
  const [from, setFrom] = useState(monthStartStr());
  const [to, setTo] = useState(todayStr());
  const [data, setData] = useState(null);
  const [workSummary, setWorkSummary] = useState(null); // 작업(WorkSchedule) 집계
  const [detailOpen, setDetailOpen] = useState(false);  // 미준수 상세 접기(기본 접힘)
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [detailPage, setDetailPage] = useState(1);
  const [pdfLoading, setPdfLoading] = useState(false);
  /*
    회사 선택 — 운영사(S/A)만 쓴다.
    S/A 로 보면 모든 회사 데이터가 한 그래프에 섞여, 고객이 둘만 돼도 숫자가 뒤죽박죽이 된다.
    고객사 계정에는 서버가 빈 목록을 주므로 이 선택 자체가 보이지 않는다.
  */
  const [companies, setCompanies] = useState([]);
  const [company, setCompany] = useState("");

  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const res = await fetch("/api/safety-report/companies", {
          credentials: "include",
          cache: "no-store",
        });
        const j = await res.json().catch(() => null);
        if (!dead) setCompanies(Array.isArray(j?.items) ? j.items : []);
      } catch {
        if (!dead) setCompanies([]);
      }
    })();
    return () => {
      dead = true;
    };
  }, []);

  const load = useCallback(async (f = from, t = to) => {
    setLoading(true);
    setError("");
    try {
      const url =
        `/api/safety-report?from=${encodeURIComponent(f)}&to=${encodeURIComponent(t)}` +
        `&site=all&limit=200` +
        (company ? `&company=${encodeURIComponent(company)}` : "");
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.message || `조회 실패 (${res.status})`);
      }
      setData(await res.json());
    } catch (e) {
      setError(String(e.message || e));
      setData(null);
    } finally {
      setLoading(false);
    }
    // 작업(WorkSchedule) 집계 — 실패해도 리포트 본문엔 영향 없음.
    try {
      const wr = await fetch(
        `/api/safety-report/work-summary?from=${encodeURIComponent(f)}&to=${encodeURIComponent(t)}`,
        { credentials: "include" }
      );
      setWorkSummary(wr.ok ? await wr.json() : null);
    } catch (_) {
      setWorkSummary(null);
    }
    // company 가 빠져 있으면 회사를 바꿔도 «이전 회사»로 조회된다.
  }, [from, to, company]);

  useEffect(() => {
    load();
    // 최초 1회만. 이후는 프리셋/조회 버튼으로.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyPreset = (p) => {
    const [f, t] = p.range();
    setFrom(f);
    setTo(t);
    load(f, t);
  };
  const activePreset = useMemo(
    () => PRESETS.find((p) => { const [f, t] = p.range(); return f === from && t === to; })?.key || null,
    [from, to]
  );

  const s = data?.summary;
  const delta = s ? s.violationCount - (s.prevViolationCount || 0) : 0;
  const violations = useMemo(() => data?.violations || [], [data]);
  const compliance = s && s.complianceRate != null ? s.complianceRate : null;
  // 피크 시간대 (미착용이 가장 많이 발생한 시각)
  const byHourArr = data?.byHour || [];
  const peakHour = byHourArr.reduce((best, h) => (h.count > (best ? best.count : 0) ? h : best), null);
  const hasPeak = !!(peakHour && peakHour.count > 0);

  // 서식 있는 엑셀(.xlsx) 다운로드 — 서버 생성(요약 KPI/인셀 데이터바 + 상세 색상표).
  const [excelLoading, setExcelLoading] = useState(false);
  const downloadReport = useCallback(async () => {
    setExcelLoading(true);
    try {
      const url = `/api/safety-report/excel?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&site=all`;
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error("엑셀 생성 실패");
      const blob = await res.blob();
      const dl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dl;
      a.download = `안전준수리포트_${from}_${to}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(dl);
    } catch (e) {
      window.alert("엑셀 생성에 실패했습니다.");
    } finally {
      setExcelLoading(false);
    }
  }, [from, to]);

  // 서버 생성 PDF 리포트 다운로드 (차트 포함, 깔끔한 A4)
  const downloadPdf = useCallback(async () => {
    setPdfLoading(true);
    try {
      const url = `/api/safety-report/pdf?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&site=all`;
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error("PDF 생성 실패");
      const blob = await res.blob();
      const dl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dl;
      a.download = `안전준수리포트_${from}_${to}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(dl);
    } catch (e) {
      window.alert("PDF 생성에 실패했습니다.");
    } finally {
      setPdfLoading(false);
    }
  }, [from, to]);

  // 미준수 상세 페이지네이션
  const detailPageCount = Math.max(1, Math.ceil(violations.length / DETAIL_PAGE_SIZE));
  const pagedViolations = useMemo(() => {
    const safe = Math.min(Math.max(detailPage, 1), detailPageCount);
    const start = (safe - 1) * DETAIL_PAGE_SIZE;
    return violations.slice(start, start + DETAIL_PAGE_SIZE);
  }, [violations, detailPage, detailPageCount]);

  useEffect(() => {
    setDetailPage(1);
  }, [data]);

  return (
    <div
      className={`flex h-full min-h-0 flex-col gap-3 ${
        detailOpen ? "overflow-y-auto pb-3" : "overflow-hidden"
      }`}
    >
      {/* 헤더 */}
      <div className="flex shrink-0 flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">안전 준수 리포트</h2>
          <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
            {data?.period ? `${data.period.from} ~ ${data.period.to}` : "기간을 선택해 조회하세요"}
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          {/* 프리셋 */}
          <div className="flex overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
            {PRESETS.map((p) => (
              <button
                key={p.key}
                onClick={() => applyPreset(p)}
                className={`px-3 py-1.5 text-xs font-semibold transition ${
                  activePreset === p.key
                    ? "bg-blue-600 text-white"
                    : "bg-white text-gray-600 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-white/5"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          {/* 회사 선택 — 운영사만 보인다(고객사는 목록이 비어 렌더되지 않음). */}
          {companies.length > 0 && (
            <select
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              className="rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
            >
              <option value="">전체 회사</option>
              {companies.map((c) => (
                <option key={c.companyIdx} value={c.companyIdx}>
                  {c.companyName}
                </option>
              ))}
            </select>
          )}

          {/* 커스텀 기간 */}
          <input
            type="date"
            value={from}
            max={to}
            onChange={(e) => setFrom(e.target.value)}
            className="rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
          />
          <span className="pb-1.5 text-gray-400">~</span>
          <input
            type="date"
            value={to}
            min={from}
            max={todayStr()}
            onChange={(e) => setTo(e.target.value)}
            className="rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
          />
          <button
            onClick={() => load()}
            disabled={loading}
            className="rounded-lg bg-blue-600 px-4 py-1.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
          >
            {loading ? "조회 중…" : "조회"}
          </button>
          <button
            onClick={downloadReport}
            disabled={!data || loading || excelLoading}
            title="서식 있는 엑셀(.xlsx)로 저장 — 요약·차트·상세"
            className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
          >
            {excelLoading ? "엑셀 생성 중…" : "⬇ 엑셀"}
          </button>
          <button
            onClick={downloadPdf}
            disabled={!data || loading || pdfLoading}
            title="차트 포함 PDF 리포트 다운로드"
            className="rounded-lg bg-[#14304d] px-3 py-1.5 text-sm font-semibold text-white hover:bg-[#1d4368] disabled:opacity-50"
          >
            {pdfLoading ? "PDF 생성 중…" : "📄 PDF 리포트"}
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300">
          {error}
        </div>
      )}

      {/* KPI — 준수율(헤드라인) · 미착용 건수(+추세) · 최다 항목 · 활성 현장 */}
      <div className="grid shrink-0 grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile
          highlight
          label="준수율"
          value={compliance != null ? compliance : "—"}
          unit={compliance != null ? "%" : ""}
          accent="text-[#0ca30c]"
          foot={
            compliance != null ? (
              <div className="mt-0.5 h-1.5 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700">
                <div className="h-full rounded-full bg-[#0ca30c]" style={{ width: `${compliance}%` }} />
              </div>
            ) : (
              "판독수 집계 시 제공"
            )
          }
        />
        <StatTile
          label="미착용 건수"
          value={s ? s.violationCount : "—"}
          unit={s ? "건" : ""}
          accent="text-gray-900 dark:text-white"
          foot={
            s ? (
              <span>
                전월 {s.prevViolationCount || 0}건
                {delta !== 0 && (
                  <span className={delta > 0 ? "ml-1 font-bold text-[#d03b3b]" : "ml-1 font-bold text-[#0ca30c]"}>
                    {delta > 0 ? `▲ ${delta}` : `▼ ${Math.abs(delta)}`}
                  </span>
                )}
                {s.falsePositiveCount > 0 && (
                  <span className="ml-1 text-[#a9720d]">· 오탐 {s.falsePositiveCount}건 제외</span>
                )}
              </span>
            ) : null
          }
        />
        <StatTile
          label="피크 시간대"
          value={hasPeak ? `${peakHour.hour}시` : "—"}
          accent="text-gray-900 dark:text-white"
          foot={hasPeak ? `${peakHour.count}건 최다 · 순찰 집중 시간` : "데이터 없음"}
        />
        <StatTile
          label="활성 현장"
          value={s ? s.activeSites : "—"}
          unit={s ? "곳" : ""}
          accent="text-gray-900 dark:text-white"
          foot={s ? "이벤트 발생 카메라 수" : null}
        />
      </div>

      {/* 작업 집계 (REQ-032~035) — WorkSchedule 기반. 위 KPI(준수율·미준수)와 중복 없이 '작업 상태/유형/차량'에 집중 */}
      {workSummary?.counts && (
        <section className="shrink-0 rounded-2xl border border-gray-200 bg-white p-3.5 dark:border-white/10 dark:bg-[#0F172A]">
          <div className="mb-2.5 flex items-center justify-between gap-2">
            <h3 className="text-base font-bold text-gray-900 dark:text-white">작업 집계</h3>
            <div className="flex items-center gap-2">
              {workSummary.jobCompliance?.rate != null && (
                <span
                  className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300"
                  title={`작업(고객건) 단위: 미착용 없는 작업 ${workSummary.jobCompliance.compliantJobs} / 전체 ${workSummary.jobCompliance.total}`}
                >
                  작업 준수율
                  <span className="tabular-nums text-emerald-600 dark:text-emerald-400">{workSummary.jobCompliance.rate}%</span>
                  <span className="font-normal text-emerald-600/70 dark:text-emerald-400/70">
                    ({workSummary.jobCompliance.compliantJobs}/{workSummary.jobCompliance.total}집)
                  </span>
                </span>
              )}
              <span className="text-xs text-gray-400">{workSummary.from} ~ {workSummary.to}</span>
            </div>
          </div>

          {/* 상태별 건수 */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            {[
              { k: "total", label: "총 작업", v: workSummary.counts.total, c: "text-gray-900 dark:text-white" },
              { k: "done", label: "완료", v: workSummary.counts.done, c: "text-emerald-600 dark:text-emerald-400" },
              { k: "working", label: "작업중", v: workSummary.counts.working, c: "text-blue-600 dark:text-blue-400" },
              { k: "waiting", label: "대기", v: workSummary.counts.waiting, c: "text-slate-500 dark:text-slate-400" },
              { k: "resch", label: "연기 요청", v: workSummary.counts.rescheduled, c: "text-amber-600 dark:text-amber-400" },
            ].map((x) => (
              <div key={x.k} className="rounded-xl bg-gray-50 px-3.5 py-2 dark:bg-white/5">
                <div className="text-[11px] text-gray-500 dark:text-gray-400">{x.label}</div>
                <div className={`text-xl font-extrabold tabular-nums ${x.c}`}>
                  {x.v}
                  <span className="ml-0.5 text-xs font-normal text-gray-400">건</span>
                </div>
              </div>
            ))}
          </div>

          {/* 유형별 + 차량별 */}
          <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-2">
            <div className="rounded-xl border border-gray-100 p-3.5 dark:border-white/10">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">유형별 (실외 / 용접)</div>
              <div className="space-y-1.5">
                {(workSummary.byType || []).map((t) => {
                  const pct = workSummary.counts.total ? Math.round((100 * t.total) / workSummary.counts.total) : 0;
                  return (
                    <div key={t.workType}>
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-medium text-gray-700 dark:text-gray-200">{t.workType}</span>
                        <span className="tabular-nums text-gray-500 dark:text-gray-400">
                          {t.total}건 · <span className="text-emerald-600 dark:text-emerald-400">완료 {t.done}</span>
                        </span>
                      </div>
                      <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-white/10">
                        <div className="h-full rounded-full bg-blue-500" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="rounded-xl border border-gray-100 p-3.5 dark:border-white/10">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">차량(팀)별 상위</div>
              <div className="space-y-1">
                {(workSummary.byVehicle || []).slice(0, 6).map((v, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <span className="truncate text-gray-700 dark:text-gray-200">
                      {v.vehicle}
                      {v.worker ? <span className="text-gray-400"> · {v.worker}</span> : ""}
                    </span>
                    <span className="shrink-0 tabular-nums text-gray-500 dark:text-gray-400">
                      {v.total}건 <span className="text-emerald-600 dark:text-emerald-400">/ {v.done}</span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* 차트 2×2 — 접힘 상태에선 남는 높이를 채우고, 펼치면 고정 높이 + 스크롤 */}
      <div
        className={`grid grid-cols-1 gap-3 lg:grid-cols-2 lg:grid-rows-2 ${
          detailOpen ? "[&>*]:min-h-[240px]" : "min-h-0 flex-1"
        }`}
      >
        <ChartCard title="항목별 미착용">
          <DonutChart
            segments={(data?.byItem || [])
              .map((i) => ({
                label: i.label,
                value: i.count,
                color: DONUT_COLOR[itemKind(i.item, i.label)] || DONUT_COLOR.other,
              }))
              .filter((s) => s.value > 0)}
          />
        </ChartCard>
        <ChartCard title="현장(카메라·팀)별 미착용">
          <HBars
            items={(data?.bySite || []).map((b) => ({ key: b.siteId, label: b.label, count: b.count }))}
          />
        </ChartCard>
        <ChartCard title="날짜별 미착용 추이" className="lg:col-span-2">
          <AreaChart
            items={(data?.daily || []).map((d) => ({
              key: d.date,
              label: d.date,
              short: d.date.slice(5),
              count: d.count,
            }))}
          />
        </ChartCard>
      </div>

      {/* 미준수 상세 — 눈에 띄는 접기 바 + 펼치면 표 */}
      <button
        type="button"
        onClick={() => setDetailOpen((o) => !o)}
        className="flex shrink-0 items-center gap-3 rounded-2xl border border-gray-200 bg-white px-4 py-3 text-left shadow-sm transition hover:border-blue-300 hover:bg-blue-50/40 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-blue-500/40 dark:hover:bg-white/5"
      >
        <span
          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-sm font-bold transition ${
            detailOpen
              ? "bg-blue-600 text-white"
              : "bg-blue-50 text-blue-600 dark:bg-blue-500/15 dark:text-blue-300"
          }`}
        >
          {detailOpen ? "▾" : "▸"}
        </span>
        <span className="whitespace-nowrap text-base font-bold text-gray-900 dark:text-white">미준수 상세</span>
        <span className="whitespace-nowrap rounded-full bg-rose-500 px-2.5 py-0.5 text-xs font-bold tabular-nums text-white">
          {data?.violationsTotal || 0}건
        </span>
        <span className="ml-auto whitespace-nowrap text-xs font-medium text-gray-400">
          {detailOpen ? "접기 ▾" : "클릭해서 펼쳐보기 ▾"}
        </span>
      </button>

      {detailOpen && (
        <Card className="shrink-0">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-xs text-gray-500 dark:border-gray-700">
                <th className="py-2 pr-3 font-medium">발생시각</th>
                <th className="py-2 pr-3 font-medium">현장/카메라</th>
                <th className="py-2 pr-3 font-medium">항목</th>
                <th className="py-2 pr-3 font-medium">상태</th>
                <th className="py-2 pr-3 font-medium">캡처</th>
              </tr>
            </thead>
            <tbody>
              {pagedViolations.map((v) => {
                const kind = itemKind(v.item, v.itemLabel);
                return (
                  <tr
                    key={v.id}
                    className="border-b border-gray-100 transition hover:bg-gray-50 dark:border-gray-700/50 dark:hover:bg-white/5"
                  >
                    <td className="whitespace-nowrap py-2 pr-3 tabular-nums text-gray-600 dark:text-gray-300">
                      {String(v.timestamp || "").replace("T", " ").slice(0, 19)}
                    </td>
                    <td className="py-2 pr-3 text-gray-600 dark:text-gray-300">{v.siteLabel}</td>
                    <td className="py-2 pr-3">
                      <span
                        className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ${ITEM_BADGE[kind]}`}
                      >
                        <span className={`h-1.5 w-1.5 rounded-full ${ITEM_DOT[kind]}`} />
                        {v.itemLabel}
                      </span>
                    </td>
                    <td className="py-2 pr-3">
                      {v.status === "done" ? (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#0ca30c]">
                          ● {STATUS_LABEL.done}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#d03b3b]">
                          ● {STATUS_LABEL.open}
                        </span>
                      )}
                    </td>
                    <td className="py-2 pr-3">
                      {v.photoUrl ? (
                        <a href={v.photoUrl} target="_blank" rel="noreferrer">
                          <img
                            src={v.photoUrl}
                            alt="캡처"
                            className="h-10 w-16 rounded object-cover ring-1 ring-gray-200 transition hover:ring-blue-400 dark:ring-gray-600"
                            loading="lazy"
                          />
                        </a>
                      ) : (
                        <span className="text-xs text-gray-400">없음</span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {!loading && !violations.length && (
                <tr>
                  <td colSpan={5} className="py-10 text-center text-gray-400">
                    해당 기간 미착용 이벤트가 없습니다.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* 페이지네이션 */}
        {violations.length > DETAIL_PAGE_SIZE && (
          <div className="mt-3 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => setDetailPage((p) => Math.max(1, p - 1))}
              disabled={detailPage <= 1}
              className="rounded-lg bg-gray-100 px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-200 disabled:cursor-default disabled:opacity-40 dark:bg-white/10 dark:text-gray-200"
            >
              ◀ 이전
            </button>
            <div className="min-w-[48px] text-center text-sm font-semibold text-gray-600 dark:text-gray-300">
              {detailPage} / {detailPageCount}
            </div>
            <button
              type="button"
              onClick={() => setDetailPage((p) => Math.min(detailPageCount, p + 1))}
              disabled={detailPage >= detailPageCount}
              className="rounded-lg bg-gray-100 px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-200 disabled:cursor-default disabled:opacity-40 dark:bg-white/10 dark:text-gray-200"
            >
              다음 ▶
            </button>
          </div>
        )}
        </Card>
      )}
    </div>
  );
}
